import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Axios from '../../../networking/intraceptor';
import { pharmacy_audit_trail_handover_requests } from '../../../networking/urlEndPoints';
import { RxNumber } from '../../../screens/pharmacy/Accountability/helper';
import { useCustomSelectors } from '../../../services/selectors/allSelectors';
import {
    getAppDateString,
    PHARMACY_HANDOVER_REQUEST_EVENT,
    PHARMACY_HANDOVER_RESPONSE_EVENT,
} from '../../../utils/miscellaneous/handoverEvents';
import {
    HandoverSignaturePad,
} from './HandoverSignatureTools';
import HandoverInventoryDecisionFields from './HandoverInventoryDecisionFields';
import {
    buildHandoverInventoryDecisions,
    handoverRequestErrorMessage,
    validateHandoverInventoryDecisions,
    needsInventoryDecision,
} from './handoverInventoryDecisions';
import useInventorySetupCompleted from './useInventorySetupCompleted';

const normalizeEventPatientsToItems = (patients = []) => (
    Array.isArray(patients)
        ? patients.flatMap((patient) => {
            const rxnumbers = Array.isArray(patient?.rxnumbers) ? patient.rxnumbers : [];
            if (rxnumbers.length === 0) {
                return [{
                    id: `patient-${patient?.patient_id ?? patient?.id ?? Math.random()}`,
                    patient_name: [patient?.last_name, patient?.first_name].filter(Boolean).join(' ') || patient?.patient_name || 'Patient',
                    route: patient?.route,
                    qty: '—',
                    drug: '—',
                    din: '—',
                    sig: '—',
                    delivered_instore_duplicate_return: Boolean(patient?.contains_duplicate_return),
                }];
            }

            return rxnumbers.map((rxnumber, index) => ({
                id: `${patient?.patient_id ?? patient?.id ?? 'patient'}-${rxnumber}-${index}`,
                rxnumber,
                patient_name: [patient?.last_name, patient?.first_name].filter(Boolean).join(' ') || patient?.patient_name || 'Patient',
                route: patient?.route,
                qty: '—',
                drug: '—',
                din: '—',
                sig: '—',
                delivered_instore_duplicate_return: Boolean(patient?.contains_duplicate_return),
            }));
        })
        : []
);

const getRequestItems = (request = {}) => {
    if (Array.isArray(request?.request_items) && request.request_items.length > 0) {
        return request.request_items;
    }

    return normalizeEventPatientsToItems(request?.patients);
};

const buildFallbackRequest = (payload = {}) => ({
    id: payload?.request_id,
    handover_type: payload?.handover_type,
    requested_by_name: payload?.requested_by?.name || 'Delivery team',
    target_name: payload?.target_name,
    status: payload?.status,
    routes: payload?.routes || [],
    route_payload: payload?.routes || [],
    patients: Array.isArray(payload?.patients) ? payload.patients : [],
    requested_at: payload?.requested_at,
    requested_at_display: payload?.requested_at_display || '—',
    sender_signature: payload?.sender_signature || null,
    inventory_setup_completed: payload?.inventory_setup_completed === true,
});

const isInventoryDecisionValidationError = (requestError) => {
    const errors = requestError?.response?.data?.data?.error;
    if (errors && typeof errors === 'object') {
        return Object.keys(errors).some((key) => String(key).startsWith('inventory_decisions'));
    }

    const message = requestError?.response?.data?.message;
    return typeof message === 'string' && message.includes('inventory_decisions');
};

const IncomingHandoverRequestManager = () => {
    const { pharmacyId, loginUserID, profileData } = useCustomSelectors();
    const verifiedInventorySetup = useInventorySetupCompleted(pharmacyId);
    const [queuedRequests, setQueuedRequests] = useState([]);
    const [loadingRequestIds, setLoadingRequestIds] = useState([]);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [receiverSignature, setReceiverSignature] = useState('');
    const [inventoryDecisions, setInventoryDecisions] = useState({});
    const [error, setError] = useState('');

    const activeRequest = queuedRequests[0] || null;
    const selectedDate = getAppDateString();
    const dateParams = useMemo(() => ({
        from_date: selectedDate,
        to_date: selectedDate,
    }), [selectedDate]);

    const removeQueuedRequest = useCallback((requestId) => {
        if (!requestId) return;
        setQueuedRequests((current) => current.filter((request) => Number(request.id) !== Number(requestId)));
        setLoadingRequestIds((current) => current.filter((id) => Number(id) !== Number(requestId)));
        setReceiverSignature('');
        setInventoryDecisions({});
    }, []);

    const enqueueRequest = useCallback((request) => {
        if (!request?.id) return;
        setQueuedRequests((current) => {
            if (current.some((item) => Number(item.id) === Number(request.id))) {
                return current.map((item) => Number(item.id) === Number(request.id) ? { ...item, ...request } : item);
            }

            return [...current, request];
        });
    }, []);

    const fetchRequestDetails = useCallback(async (requestId, payload) => {
        if (!pharmacyId || !loginUserID || !requestId) {
            enqueueRequest(buildFallbackRequest(payload));
            return;
        }

        setLoadingRequestIds((current) => current.includes(requestId) ? current : [...current, requestId]);
        try {
            const response = await Axios.get(pharmacy_audit_trail_handover_requests, { params: dateParams });
            const requests = Array.isArray(response?.data?.data) ? response.data.data : [];
            const matchedRequest = requests.find((request) => Number(request.id) === Number(requestId));
            enqueueRequest(matchedRequest || buildFallbackRequest(payload));
        } catch {
            enqueueRequest(buildFallbackRequest(payload));
        } finally {
            setLoadingRequestIds((current) => current.filter((id) => Number(id) !== Number(requestId)));
        }
    }, [dateParams, enqueueRequest, loginUserID, pharmacyId]);

    useEffect(() => {
        const handleIncomingRequest = (event) => {
            const payload = event?.detail;
            if (
                profileData?.type !== 'pharmacist'
                || Number(payload?.target_id) !== Number(loginUserID)
                || getAppDateString(payload?.requested_at || new Date()) !== selectedDate
            ) {
                return;
            }

            setError('');
            fetchRequestDetails(payload?.request_id, payload);
        };

        const handleRequestResponse = (event) => {
            const payload = event?.detail;
            if (!payload?.request_id) {
                return;
            }

            removeQueuedRequest(payload.request_id);
        };

        window.addEventListener(PHARMACY_HANDOVER_REQUEST_EVENT, handleIncomingRequest);
        window.addEventListener(PHARMACY_HANDOVER_RESPONSE_EVENT, handleRequestResponse);

        return () => {
            window.removeEventListener(PHARMACY_HANDOVER_REQUEST_EVENT, handleIncomingRequest);
            window.removeEventListener(PHARMACY_HANDOVER_RESPONSE_EVENT, handleRequestResponse);
        };
    }, [fetchRequestDetails, loginUserID, profileData?.type, removeQueuedRequest, selectedDate]);

    const handleRequestAction = useCallback(async (requestId, action, signatureBase64 = '', requestItems = [], inventoryEnabled = false) => {
        if (!requestId || actionLoadingId) {
            return false;
        }

        if (action === 'accept' && !signatureBase64) {
            setError('Signature is required.');
            return false;
        }

        // A half-filled "some damaged" line must not reach the server: say
        // what is missing here, before the signature is spent on the request.
        if (action === 'accept' && inventoryEnabled) {
            const inventoryError = validateHandoverInventoryDecisions(requestItems, inventoryDecisions);
            if (inventoryError) {
                setError(inventoryError);
                return false;
            }
        }

        setActionLoadingId(requestId);
        setError('');

        try {
            const payload = action === 'accept'
                ? {
                    signature_base64: signatureBase64,
                    ...(inventoryEnabled ? {
                        inventory_decisions: buildHandoverInventoryDecisions(requestItems, inventoryDecisions),
                    } : {}),
                }
                : {};
            await Axios.post(`${pharmacy_audit_trail_handover_requests}/${requestId}/${action}`, payload);
            window.dispatchEvent(new CustomEvent(PHARMACY_HANDOVER_RESPONSE_EVENT, {
                detail: {
                    request_id: requestId,
                    action,
                    status: action === 'accept' ? 'accepted' : 'rejected',
                    source: 'local',
                    responded_at: new Date().toISOString(),
                },
            }));
            removeQueuedRequest(requestId);
            return true;
        } catch (requestError) {
            if (isInventoryDecisionValidationError(requestError)) {
                setError('');
                return false;
            }

            setError(handoverRequestErrorMessage(requestError, `Unable to ${action} the handover request.`));
            return false;
        } finally {
            setActionLoadingId(null);
        }
    }, [actionLoadingId, inventoryDecisions, removeQueuedRequest]);

    const handleDismiss = () => {
        if (activeRequest?.id) {
            removeQueuedRequest(activeRequest.id);
        }
    };

    if (!activeRequest) {
        return null;
    }

    const requestItems = getRequestItems(activeRequest);
    const inventorySetupCompleted = verifiedInventorySetup === true
        || (verifiedInventorySetup === null && activeRequest?.inventory_setup_completed === true);
    const hasInventoryReturn = inventorySetupCompleted && requestItems.length > 0;
    const isRequestLoading = loadingRequestIds.includes(activeRequest.id);
    const isActionLoading = Number(actionLoadingId) === Number(activeRequest.id);
    const isCompactRequest = requestItems.length > 0 && requestItems.length <= 3;
    const modalHeightClass = isCompactRequest
        ? 'max-h-[calc(100vh-1.5rem)]'
        : 'h-[calc(100vh-1.5rem)] max-h-[calc(100vh-1.5rem)]';
    const modalGridHeightClass = isCompactRequest
        ? 'max-h-[calc(100vh-1.5rem)]'
        : 'h-full max-h-[calc(100vh-1.5rem)]';
    const requestListClass = isCompactRequest
        ? 'mt-5 min-h-0 pr-2'
        : 'mt-5 min-h-0 overflow-y-auto pr-2 md:flex-1';

    return (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center overflow-hidden bg-slate-950/60 p-3 backdrop-blur-md">
            <div className={`flex ${modalHeightClass} w-full max-w-[86rem] overflow-hidden rounded-[28px] border border-white/15 bg-[linear-gradient(180deg,rgba(15,23,42,0.98)_0%,rgba(15,23,42,0.94)_100%)] text-white shadow-[0_28px_80px_rgba(15,23,42,0.45)]`}>
                <div className={`relative grid ${modalGridHeightClass} min-h-0 w-full grid-cols-1 overflow-hidden md:grid-cols-[minmax(0,1fr)_330px] lg:grid-cols-[minmax(0,1fr)_350px]`}>
                    <button
                        type="button"
                        onClick={handleDismiss}
                        className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-slate-200 transition hover:bg-white/10"
                        aria-label="Close handover request"
                    >
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6l-12 12" />
                        </svg>
                    </button>

                    {/* Left panel */}
                    <div className="flex min-h-0 flex-col px-5 py-6 md:px-6 md:py-7">
                        <div className="shrink-0 pr-14">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-200/80">Incoming Handover Request</p>
                            <h2 className="text-[24px] leading-tight font-semibold text-white">
                                {activeRequest?.requested_by_name || 'Delivery team'}
                            </h2>
                            <p className="text-sm text-slate-300">
                                Date: {activeRequest?.requested_at_display || '—'}
                            </p>
                        </div>

                        <div className={requestListClass}>
                            {isRequestLoading ? (
                                <div className="flex items-center justify-center py-10">
                                    <div className="h-7 w-7 animate-spin rounded-full border-2 border-slate-500 border-t-sky-300" />
                                </div>
                            ) : requestItems.length === 0 ? (
                                <p className="text-sm text-slate-300">No medication rows available.</p>
                            ) : (
                                <div className="overflow-x-auto rounded-xl border border-white/10 bg-slate-950/20">
                                    <table className="w-full min-w-[960px] table-fixed text-left text-sm text-slate-200">
                                        <colgroup>
                                            <col className="w-[82px]" />
                                            <col className="w-[136px]" />
                                            <col className={hasInventoryReturn ? 'w-[380px]' : 'w-[620px]'} />
                                            <col className="w-[70px]" />
                                            {hasInventoryReturn ? <col className="w-[292px]" /> : null}
                                        </colgroup>
                                        <thead className="bg-white/5">
                                            <tr className="border-b border-white/10 text-xs font-semibold uppercase tracking-wide text-slate-400">
                                                <th className="px-3 py-2">RX</th>
                                                <th className="px-3 py-2">Patient Name</th>
                                                <th className="px-3 py-2">Drug / DIN</th>
                                                <th className="px-3 py-2 text-right">Qty</th>
                                                {hasInventoryReturn ? <th className="min-w-[270px] px-3 py-2">Inventory decision</th> : null}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {requestItems.map((item) => {
                                                const rowKey = item?.id || `${item?.patient_id}-${item?.rxnumber}`;
                                                const showDeliveredReturnBadge = inventorySetupCompleted && item?.delivered_instore_duplicate_return;
                                                const showInventoryDecisionBadge = hasInventoryReturn && needsInventoryDecision(item);
                                                const showReturnBadgeRow = showDeliveredReturnBadge || showInventoryDecisionBadge;

                                                return (
                                                <React.Fragment key={rowKey}>
                                                <tr className="border-t border-white/10">
                                                    <td className="px-3 py-2 align-top font-medium text-white">
                                                        {item?.rxnumber ? (
                                                            <RxNumber
                                                                rx_number={item.rxnumber}
                                                                className_1="font-medium text-white inline"
                                                                as="span"
                                                            />
                                                        ) : '—'}
                                                    </td>
                                                    <td className="px-3 py-2 align-top">{item?.patient_name || 'Patient'}</td>
                                                    <td className="px-3 py-2 align-top">
                                                        <div className="max-w-[340px]">
                                                            <p className="whitespace-normal break-words leading-5">
                                                                {item?.drug || '—'}{item?.din ? ` - ${item.din}` : ''}
                                                            </p>
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2 align-top text-right">
                                                        {item?.qty ?? '—'}
                                                    </td>
                                                    {hasInventoryReturn ? (
                                                        <td className="min-w-[270px] px-3 py-2 align-top">
                                                            <HandoverInventoryDecisionFields
                                                                dark
                                                                embedded
                                                                items={[item]}
                                                                value={inventoryDecisions}
                                                                onChange={(nextValue) => {
                                                                    setInventoryDecisions(nextValue);
                                                                    setError('');
                                                                }}
                                                            />
                                                        </td>
                                                    ) : null}
                                                </tr>
                                                {showReturnBadgeRow ? (
                                                    <tr>
                                                        <td colSpan={hasInventoryReturn ? 5 : 4} className="px-3 pb-2 pt-0">
                                                            <div className="flex w-full max-w-full min-w-0 items-center gap-1 overflow-hidden pl-0">
                                                                {showDeliveredReturnBadge ? (
                                                                    <span className="inline-flex min-w-0 shrink items-center truncate whitespace-nowrap rounded bg-yellow-100 px-2 py-0.5 text-[10px] font-semibold leading-5 text-yellow-800">
                                                                        Delivered In-Store - Stock return by driver.
                                                                    </span>
                                                                ) : null}
                                                                {showInventoryDecisionBadge ? (
                                                                    <span className="inline-flex min-w-0 shrink items-center truncate whitespace-nowrap rounded bg-yellow-100 px-2 py-0.5 text-[10px] font-semibold leading-5 text-yellow-800">
                                                                        Returned medication - awaiting pharmacist inventory decision.
                                                                    </span>
                                                                ) : null}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : null}
                                                </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right panel */}
                    <div className="flex min-h-0 flex-col border-t border-white/10 px-5 py-6 md:border-l md:border-t-0 md:border-white/10 md:px-6 md:py-7">
                        <div className="shrink-0 pr-14">
                            <h3 className="text-lg font-semibold text-white">Confirm Receipt</h3>
                            <p className="text-sm text-slate-300">
                                {hasInventoryReturn ? 'Complete each medication decision on the left, then sign to accept.' : 'Sign to accept this handover.'}
                            </p>
                        </div>

                        {hasInventoryReturn ? (
                            <div className="mt-4 shrink-0 rounded-2xl border border-amber-300/25 bg-amber-400/10 px-4 py-3 text-sm text-amber-50">
                                <p className="font-semibold">Inventory Impact</p>
                                <p className="mt-1 text-amber-100/90">
                                    Mark each medication as usable or unusable. Usable is selected by default.
                                </p>
                            </div>
                        ) : null}

                        <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
                            <HandoverSignaturePad
                                title=""
                                description=""
                                value={receiverSignature}
                                onChange={(nextValue) => {
                                    setReceiverSignature(nextValue);
                                    if (error === 'Signature is required.') {
                                        setError('');
                                    }
                                }}
                                className="border-white/10 bg-white/5"
                            />
                        </div>

                        {error ? (
                            <div className="mt-4 rounded-xl border border-red-400/20 bg-red-500/10 px-4 py-2.5 text-sm text-red-200">
                                {error}
                            </div>
                        ) : null}

                        <div className="-mx-5 -mb-6 mt-6 flex shrink-0 items-center justify-end gap-3 border-t border-white/10 bg-slate-900/95 px-5 py-4 backdrop-blur md:-mx-6 md:-mb-7 md:px-6">

                            <button
                                type="button"
                                onClick={() => handleRequestAction(activeRequest.id, 'reject')}
                                disabled={isActionLoading}
                                className={` min-w-[116px] rounded-2xl px-5 py-3.5 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-red-500/20 ${isActionLoading
                                        ? 'cursor-not-allowed border border-red-500/20 bg-white/5 text-red-200/60'
                                        : 'border border-white/10 bg-white/5 text-red-400 hover:border-white/15 hover:bg-white/10'
                                    }`}
                            >
                                {hasInventoryReturn ? 'Reject' : 'Decline'}
                            </button>

                            <button
                                type="button"
                                onClick={() => handleRequestAction(activeRequest.id, 'accept', receiverSignature, requestItems, inventorySetupCompleted)}
                                disabled={isActionLoading}
                                className={` min-w-[174px] rounded-2xl px-5 py-3.5 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-green-400/40 ${isActionLoading
                                        ? 'cursor-not-allowed bg-green-500 text-emerald-100'
                                        : 'bg-green-500 text-white hover:bg-green-500'
                                    }`}
                            >
                                {isActionLoading ? (
                                    <span className="inline-flex items-center justify-center gap-2">
                                        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-emerald-100/70 border-t-transparent" />
                                        Accepting...
                                    </span>
                                ) : (
                                    'Accept'
                                )}
                            </button>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IncomingHandoverRequestManager;
